import React from 'react';
import { HelpCircle, Radar } from 'lucide-react';
import { motion } from 'motion/react';

export const TooltipHelp = ({ text }: { text: string }) => (
  <div className="relative group/tt flex items-center ml-1">
    <HelpCircle className="w-4 h-4 text-white/30 cursor-help hover:text-white transition-colors" />
    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-max max-w-[280px] p-3 bg-black/95 backdrop-blur-xl border border-white/20 rounded-xl text-[11px] leading-relaxed text-white/90 shadow-[0_20px_50px_rgba(0,0,0,0.5)] opacity-0 invisible group-hover/tt:opacity-100 group-hover/tt:visible transition-all duration-200 z-[100] pointer-events-none whitespace-normal break-words">
      <div className="relative z-[101]">
        {text}
      </div>
      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-black/90 rotate-45 border-r border-b border-white/20" />
    </div>
  </div>
);

export function ScannerPulse() {
  return (
    <div className="relative w-24 h-24 mb-6 flex items-center justify-center">
      <motion.div
        animate={{ scale: [1, 2], opacity: [0.5, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
        className="absolute inset-0 bg-blue-500/20 rounded-full border border-blue-500/50"
      />
      <motion.div
        animate={{ scale: [1, 2.5], opacity: [0.3, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 0.6 }}
        className="absolute inset-0 bg-blue-400/10 rounded-full border border-blue-400/30"
      />
      <motion.div
        animate={{ scale: [1, 3], opacity: [0.1, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 1.2 }}
        className="absolute inset-0 bg-blue-300/5 rounded-full border border-blue-300/20"
      />
      <div className="relative z-10 p-4 bg-blue-500/10 rounded-full border border-blue-500/30 shadow-[0_0_30px_rgba(59,130,246,0.3)]">
        <Radar className="w-8 h-8 text-blue-400" />
      </div>
    </div>
  );
}

export function RegimeBadge({ regime, lang = 'ko' }: { regime: string, lang?: string }) {
  const getRegimeColor = (r: string) => {
    switch (r) {
      case 'Goldilocks': return 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.1)]';
      case 'Reflation': return 'bg-blue-500/10 border-blue-500/20 text-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.1)]';
      case 'Stagflation': return 'bg-red-500/10 border-red-500/20 text-red-400 shadow-[0_0_15px_rgba(239,68,68,0.1)]';
      case 'Deflation': return 'bg-amber-500/10 border-amber-500/20 text-amber-400 shadow-[0_0_15px_rgba(245,158,11,0.1)]';
      default: return 'bg-slate-500/10 border-slate-500/20 text-slate-400';
    }
  };

  const translateRegime = (r: string) => {
    if (lang !== 'ko') return r;
    const mapping: Record<string, string> = {
      'Goldilocks': '골디락스',
      'Reflation': '리플레이션',
      'Stagflation': '스태그플레이션',
      'Deflation': '디플레이션',
      'Neutral': '중립'
    };
    return mapping[r] || r;
  };

  const getRegimeDesc = (r: string) => {
    const desc: Record<string, { ko: string, en: string }> = {
      'Goldilocks': { ko: '저물가 고성장 (이상적 환경)', en: 'Low Inflation / High Growth' },
      'Reflation': { ko: '물가 상승 / 경기 회복', en: 'Rising Inflation / Growth' },
      'Stagflation': { ko: '고물가 저성장 (최악의 환경)', en: 'High Inflation / Low Growth' },
      'Deflation': { ko: '물가 하락 / 경기 침체', en: 'Falling Inflation / Low Growth' },
      'Neutral': { ko: '방향성 탐색 중', en: 'Neutral / Searching for Direction' }
    };
    return lang === 'ko' ? desc[r]?.ko : desc[r]?.en;
  };

  return (
    <div className="flex items-center gap-2 group/rb">
      <div className={`px-2.5 py-1 rounded-lg border text-[10px] font-black uppercase tracking-wider flex items-center gap-2 transition-all hover:ring-2 ring-current/20 cursor-default ${getRegimeColor(regime)}`}>
        <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${regime === 'Stagflation' ? 'bg-red-400 shadow-[0_0_8px_rgba(239,68,68,0.5)]' : regime === 'Goldilocks' ? 'bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-current shadow-[0_0_8px_rgba(255,255,255,0.3)]'}`} />
        {translateRegime(regime)}
      </div>
      <div className="hidden md:block overflow-hidden max-w-0 group-hover/rb:max-w-[300px] transition-all duration-500 ease-in-out whitespace-nowrap">
        <span className="text-[10px] text-white/40 font-medium pl-1 italic">
          {getRegimeDesc(regime)}
        </span>
      </div>
    </div>
  );
}
